/**
 * The browser page: the kiosk canvas and the panel that is open (dev tools, the replay controls, the gym review,
 * the operator's console), never over the canvas when the panel is closed. Where the panel goes is decided by the
 * space the page has (page-layout.ts): beside the canvas when there is room, below it as a sheet otherwise — a phone,
 * a tablet held upright, the totem itself. Ctrl+D toggles the dev panel.
 */
import { lazy, Suspense, useEffect, useState, type CSSProperties } from 'react';
import { useApp } from '../react/app';
import { useUi } from '../react/hooks';
import { useViewport } from '../react/useViewport';

import { BottomSheet } from './BottomSheet';
import { CrashBanner } from './CrashBanner';
import { ErrorBoundary } from './ErrorBoundary';
import { pageLayout, type SheetState } from './page-layout';
import { Shell } from './Shell';
import { useUiActions } from './useUiActions';
import css from './Shell.module.css';

// The panels are developer and operator tools: their own chunks, which the totem never loads.
const DevPanel = lazy(() => import('../devtools/DevPanel').then((m) => ({ default: m.DevPanel })));
const OperatorPanel = lazy(() => import('../operator/OperatorPanel').then((m) => ({ default: m.OperatorPanel })));
const ReplayPanel = lazy(() => import('../replay/ReplayPanel').then((m) => ({ default: m.ReplayPanel })));
const ReviewPanel = lazy(() => import('../review/ReviewPanel').then((m) => ({ default: m.ReviewPanel })));

const PANEL_NAMES = { none: '', dev: 'Developer tools', replay: 'Replay', review: 'Review', operator: 'Operator' } as const;
// How a sheet below the canvas opens, per panel: a replay needs only its transport, the others are worked in.
const FIRST_SHEET: Record<keyof typeof PANEL_NAMES, SheetState> = { none: 'peek', dev: 'half', replay: 'peek', review: 'half', operator: 'half' };

export function Page() {
  const { kiosk, replay, review } = useApp();
  const panel = useUi((u) => u.panel);
  const actions = useUiActions();
  const viewport = useViewport();
  const [sheet, setSheet] = useState<{ panel: typeof panel; state: SheetState }>({ panel, state: FIRST_SHEET[panel] });
  // another panel opens as that panel opens, not as the last one was left
  const sheetState = sheet.panel === panel ? sheet.state : FIRST_SHEET[panel];
  const layout = pageLayout(viewport, panel !== 'none', sheetState);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        actions.togglePanel('dev');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const content = (
    <ErrorBoundary name={`panel ${panel}`}>
      <Suspense fallback={null}>
        {panel === 'dev' && <DevPanel />}
        {panel === 'replay' && replay && <ReplayPanel />}
        {panel === 'review' && review && <ReviewPanel />}
        {panel === 'operator' && kiosk.role === 'operator' && <OperatorPanel />}
      </Suspense>
    </ErrorBoundary>
  );

  // the closed sheet keeps its band below the canvas: opening it never moves or resizes the canvas
  const style = layout.placement === 'bottom' ? ({ '--sheet-peek': `${layout.peekHeight}px` } as CSSProperties) : undefined;
  return (
    <div className={css.page} data-placement={layout.placement} style={style}>
      <ErrorBoundary name="shell">
        <Shell />
      </ErrorBoundary>
      {layout.placement === 'side' && (
        <aside className={css.side} style={{ width: layout.panelWidth }} aria-label={PANEL_NAMES[panel]}>{content}</aside>
      )}
      {layout.placement === 'bottom' && (
        <BottomSheet state={sheetState} height={layout.sheetHeight} viewport={viewport}
          onState={(state) => setSheet({ panel, state })} label={PANEL_NAMES[panel]}>
          {content}
        </BottomSheet>
      )}
      <CrashBanner />
    </div>
  );
}
